import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { api } from '../api/axiosManager';
import { logout } from './authSlice';
import toast from 'react-hot-toast';
import { mapRoleFromAPI as mapRoleFields } from '../../utils/roleDisplay';

// .NET tarih formatını parse et: /Date(1763710568331)/ -> Date object
const parseDotNetDate = (dateString) => {
  if (!dateString) return null;
  try {
    const dotNetMatch = dateString.match(/\/Date\((\d+)\)\//);
    if (dotNetMatch) {
      return new Date(parseInt(dotNetMatch[1]));
    }
    return new Date(dateString);
  } catch (error) {
    console.error('Tarih parse hatası:', error);
    return null;
  }
};

// Backend API'den gelen rol verisini frontend formatına çevir
const mapRoleFromAPI = (role) => {
  const mapped = mapRoleFields(role);
  return {
    ...mapped,
    createdAt: mapped.createdAt ? parseDotNetDate(mapped.createdAt) : null,
    updatedAt: mapped.updatedAt ? parseDotNetDate(mapped.updatedAt) : null,
  };
};

const mapAdminUserFromAPI = (user) => {
  if (!user || typeof user !== 'object') return user;
  const roles = Array.isArray(user.roles)
    ? user.roles.map((role) => mapRoleFromAPI(role))
    : [];
  return {
    ...user,
    roles,
  };
};

// ==================== ROL YÖNETİMİ (Backend API Kullanımı) ====================

// Rolleri çek
export const fetchRoles = createAsyncThunk(
  'admin/fetchRoles',
  async ({ search = '', page = 1, limit = 100 } = {}, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams({ 
        page: page.toString(), 
        limit: limit.toString() 
      });
      if (search) params.append('search', search);
      
      const response = await api.get(`/api/admin/roles?${params.toString()}`);
      
      if (response.data.success && response.data.data) {
        // Backend'den gelen veriyi frontend formatına çevir
        const mappedRoles = response.data.data.map(mapRoleFromAPI);
        
        return {
          data: mappedRoles,
          pagination: response.data.pagination || {
            page,
            limit,
            total: mappedRoles.length,
            totalPages: Math.ceil(mappedRoles.length / limit),
          },
        };
      }
      
      throw new Error('Geçersiz yanıt formatı');
    } catch (error) {
      console.error('❌ Fetch Roles Error:', error);
      return rejectWithValue(error.response?.data?.message || error.message || 'Roller yüklenemedi');
    }
  }
);

// Rol detayı çek
export const fetchRoleById = createAsyncThunk(
  'admin/fetchRoleById',
  async (roleId, { rejectWithValue }) => {
    try {
      const response = await api.get(`/api/admin/roles/${roleId}`);
      
      if (response.data.success && response.data.data) {
        return mapRoleFromAPI(response.data.data);
      }
      
      return rejectWithValue('Rol bulunamadı');
    } catch (error) {
      console.error('❌ Fetch Role Error:', error);
      return rejectWithValue(error.response?.data?.message || error.message || 'Rol detayı yüklenemedi');
    }
  }
);

// Yeni rol oluştur
export const createRole = createAsyncThunk(
  'admin/createRole',
  async (roleData, { rejectWithValue, getState }) => {
    try {
      // Backend API formatına çevir
      const payload = {
        name: roleData.name || roleData.displayName,
        displayName: roleData.displayName || roleData.name,
        description: roleData.description || '',
        permissions: Array.isArray(roleData.permissions)
          ? roleData.permissions
          : roleData.permissions
            ? String(roleData.permissions).split(',').map((p) => p.trim()).filter(Boolean)
            : [],
      };

      const response = await api.post('/api/admin/roles', payload);

      if (response.data.success && response.data.data) {
        toast.success('Rol başarıyla oluşturuldu');
        const mapped = mapRoleFromAPI(response.data.data);
        if (
          payload.displayName &&
          payload.displayName !== mapped.name &&
          (!mapped.displayName || mapped.displayName === mapped.name)
        ) {
          return { ...mapped, displayName: payload.displayName };
        }
        return mapped;
      }
      
      throw new Error('Rol oluşturulamadı');
    } catch (error) {
      const errorMsg = error.response?.data?.message || error.message || 'Rol oluşturulamadı';
      toast.error(errorMsg);
      return rejectWithValue(errorMsg);
    }
  }
);

// Rolü güncelle
export const updateRole = createAsyncThunk(
  'admin/updateRole',
  async ({ roleId, ...updateData }, { rejectWithValue }) => {
    try {
      const payload = {};
      if (updateData.displayName !== undefined && updateData.displayName !== null) {
        payload.displayName = String(updateData.displayName).trim();
      }
      if (updateData.description !== undefined) {
        payload.description = updateData.description;
      }
      if (updateData.permissions !== undefined) {
        payload.permissions = Array.isArray(updateData.permissions)
          ? updateData.permissions
          : updateData.permissions
            ? String(updateData.permissions).split(',').map((p) => p.trim()).filter(Boolean)
            : [];
      }

      const response = await api.put(`/api/admin/roles/${roleId}`, payload);

      if (response.data.success && response.data.data) {
        toast.success('Rol başarıyla güncellendi');
        const mapped = mapRoleFromAPI(response.data.data);
        // Backend PUT bazen displayName'i name slug'ı olarak döndürüyor; formdan geleni koru.
        if (
          payload.displayName &&
          payload.displayName !== mapped.name &&
          (!mapped.displayName || mapped.displayName === mapped.name)
        ) {
          return { ...mapped, displayName: payload.displayName };
        }
        return mapped;
      }
      
      throw new Error('Rol güncellenemedi');
    } catch (error) {
      const errorMsg = error.response?.data?.message || error.message || 'Rol güncellenemedi';
      toast.error(errorMsg);
      return rejectWithValue(errorMsg);
    }
  }
);

// Rolü sil
export const deleteRole = createAsyncThunk(
  'admin/deleteRole',
  async (roleId, { rejectWithValue }) => {
    try {
      const response = await api.delete(`/api/admin/roles/${roleId}`);
      
      if (response.data.success) {
        toast.success('Rol başarıyla silindi');
        return roleId;
      }
      
      throw new Error('Rol silinemedi');
    } catch (error) {
      const errorMsg = error.response?.data?.message || error.message || 'Rol silinemedi';
      toast.error(errorMsg);
      return rejectWithValue(errorMsg);
    }
  }
);

// Rol kullanıcılarını çek
export const fetchRoleUsers = createAsyncThunk(
  'admin/fetchRoleUsers',
  async (roleId, { rejectWithValue }) => {
    try {
      const response = await api.get(`/api/admin/roles/${roleId}/users`);
      
      if (response.data.success && response.data.data) {
        return {
          role: mapRoleFromAPI(response.data.data.role),
          users: response.data.data.users || [],
        };
      }
      
      return rejectWithValue('Rol kullanıcıları bulunamadı');
    } catch (error) {
      console.error('❌ Fetch Role Users Error:', error);
      return rejectWithValue(error.response?.data?.message || error.message || 'Rol kullanıcıları yüklenemedi');
    }
  }
);

// ==================== ADMIN KULLANICI YÖNETİMİ ====================

// Admin kullanıcılarını çek
export const fetchAdminUsers = createAsyncThunk(
  'admin/fetchAdminUsers',
  async ({ search = '', status = 'all', page = 1, limit = 100 } = {}, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams({ page: page.toString(), limit: limit.toString() });
      if (search) params.append('search', search);
      if (status !== 'all') params.append('status', status);
      
      const response = await api.get(`/api/admin/admin-users?${params.toString()}`);
      const users = (response.data.data || []).map(mapAdminUserFromAPI);
      return {
        data: users,
        pagination: response.data.pagination || {},
      };
    } catch (error) {
      console.error('❌ Fetch Admin Users Error:', error);
      return rejectWithValue(error.response?.data || 'Yönetici kullanıcıları yüklenemedi');
    }
  }
);

// Admin kullanıcı detayı çek
export const fetchAdminUserById = createAsyncThunk(
  'admin/fetchAdminUserById',
  async (userId, { rejectWithValue }) => {
    try {
      const response = await api.get(`/api/admin/admin-users/${userId}`);
      return mapAdminUserFromAPI(response.data.data);
    } catch (error) {
      console.error('❌ Fetch Admin User Error:', error);
      return rejectWithValue(error.response?.data || 'Yönetici detayı yüklenemedi');
    }
  }
);

// Yeni admin kullanıcı oluştur
export const createAdminUser = createAsyncThunk(
  'admin/createAdminUser',
  async (userData, { rejectWithValue }) => {
    try {
      const response = await api.post('/api/admin/admin-users', userData);
      toast.success('Yönetici başarıyla oluşturuldu');
      return mapAdminUserFromAPI(response.data.data);
    } catch (error) {
      const errorMsg = error.response?.data?.message || 'Yönetici oluşturulamadı';
      toast.error(errorMsg);
      return rejectWithValue(errorMsg);
    }
  }
);

// Admin kullanıcıyı güncelle
export const updateAdminUser = createAsyncThunk(
  'admin/updateAdminUser',
  async ({ userId, ...updateData }, { rejectWithValue }) => {
    try {
      const response = await api.put(`/api/admin/admin-users/${userId}`, updateData);
      toast.success('Yönetici başarıyla güncellendi');
      return mapAdminUserFromAPI(response.data.data);
    } catch (error) {
      const errorMsg = error.response?.data?.message || 'Yönetici güncellenemedi';
      toast.error(errorMsg);
      return rejectWithValue(errorMsg);
    }
  }
);

// Admin kullanıcıyı sil
export const deleteAdminUser = createAsyncThunk(
  'admin/deleteAdminUser',
  async (userId, { rejectWithValue, getState }) => {
    try {
      // Mevcut kullanıcının ID'sini kontrol et
      const state = getState();
      const currentUserId = state.auth?.user?.id;
      
      // Silme işlemini yap (401 hatası gelirse normal hata olarak handle et)
      await api.delete(`/api/admin/admin-users/${userId}`);
      
      // Eğer kendi kullanıcısını sildiyse logout yap
      if (currentUserId === userId) {
        console.warn('⚠️ Kullanıcı kendi hesabını sildi, logout yapılıyor');
        // Sayfayı yenile (logout action'ı tetiklenecek)
        window.location.href = '/login';
        return userId; // Return yap ki state güncellenmesin
      }

      return userId;
    } catch (error) {
      // Silme işlemi hatası - normal hata olarak handle et
      // DELETE işlemleri için 401 hatası token geçersizliği değil, silme işleminin bir parçası olabilir
      const errorMsg = error.response?.data?.message || error.message || 'Yönetici silinemedi';
      return rejectWithValue(errorMsg);
    }
  }
);

// Kullanıcıya rol ata
export const assignRolesToUser = createAsyncThunk(
  'admin/assignRolesToUser',
  async ({ userId, roleIds }, { rejectWithValue }) => {
    try {
      const response = await api.post(`/api/admin/admin-users/${userId}/roles`, { roleIds });
      toast.success('Roller başarıyla atandı');
      return { userId, roles: (response.data.data || []).map((role) => mapRoleFromAPI(role)) };
    } catch (error) {
      const errorMsg = error.response?.data?.message || 'Roller atanamadı';
      toast.error(errorMsg);
      return rejectWithValue(errorMsg);
    }
  }
);

// Kullanıcıdan rol kaldır
export const removeRoleFromUser = createAsyncThunk(
  'admin/removeRoleFromUser',
  async ({ userId, roleId }, { rejectWithValue }) => {
    try {
      await api.delete(`/api/admin/admin-users/${userId}/roles/${roleId}`);
      toast.success('Rol başarıyla kaldırıldı');
      return { userId, roleId };
    } catch (error) {
      const errorMsg = error.response?.data?.message || 'Rol kaldırılamadı';
      toast.error(errorMsg);
      return rejectWithValue(errorMsg);
    }
  }
);

// ==================== PERMISSION YÖNETİMİ ====================

// Tüm permission'ları çek
export const fetchPermissions = createAsyncThunk(
  'admin/fetchPermissions',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/api/admin/permissions');
      return response.data.data || [];
    } catch (error) {
      console.error('❌ Fetch Permissions Error:', error);
      return rejectWithValue(error.response?.data || 'Yetkiler yüklenemedi');
    }
  }
);

// Initial State
const initialState = {
  // Roller
  roles: [],
  selectedRole: null,
  rolesLoading: false,
  rolesError: null,
  rolesPagination: {},
  
  // Admin Kullanıcılar
  adminUsers: [],
  selectedAdminUser: null,
  adminUsersLoading: false,
  adminUsersError: null,
  adminUsersPagination: {},
  
  // Permissions
  permissions: [],
  permissionsLoading: false,
  permissionsError: null,
  
  // Rol Kullanıcıları
  roleUsers: [],
  roleUsersLoading: false,
  roleUsersError: null,
};

// Admin Slice
const adminSlice = createSlice({
  name: 'admin',
  initialState,
  reducers: {
    clearRolesError: (state) => {
      state.rolesError = null;
    },
    clearAdminUsersError: (state) => {
      state.adminUsersError = null;
    },
    setSelectedRole: (state, action) => {
      state.selectedRole = action.payload;
    },
    setSelectedAdminUser: (state, action) => {
      state.selectedAdminUser = action.payload;
    },
  },
  extraReducers: (builder) => {
    // ==================== ROL YÖNETİMİ ====================
    
    // Fetch Roles
    builder
      .addCase(fetchRoles.pending, (state) => {
        state.rolesLoading = true;
        state.rolesError = null;
      })
      .addCase(fetchRoles.fulfilled, (state, action) => {
        state.rolesLoading = false;
        state.roles = action.payload.data;
        state.rolesPagination = action.payload.pagination;
        state.rolesError = null;
      })
      .addCase(fetchRoles.rejected, (state, action) => {
        state.rolesLoading = false;
        state.rolesError = action.payload;
      });

    // Fetch Role By ID
    builder
      .addCase(fetchRoleById.pending, (state) => {
        state.rolesLoading = true;
        state.rolesError = null;
      })
      .addCase(fetchRoleById.fulfilled, (state, action) => {
        state.rolesLoading = false;
        state.selectedRole = action.payload;
        state.rolesError = null;
        // Güncellenmiş rolü listede de güncelle
        const index = state.roles.findIndex(r => r.id === action.payload.id);
        if (index !== -1) {
          state.roles[index] = action.payload;
        }
      })
      .addCase(fetchRoleById.rejected, (state, action) => {
        state.rolesLoading = false;
        state.rolesError = action.payload;
      });

    // Create Role
    builder
      .addCase(createRole.pending, (state) => {
        state.rolesLoading = true;
        state.rolesError = null;
      })
      .addCase(createRole.fulfilled, (state, action) => {
        state.rolesLoading = false;
        state.roles = [action.payload, ...state.roles];
        state.rolesError = null;
      })
      .addCase(createRole.rejected, (state, action) => {
        state.rolesLoading = false;
        state.rolesError = action.payload;
      });

    // Update Role
    builder
      .addCase(updateRole.pending, (state) => {
        state.rolesLoading = true;
        state.rolesError = null;
      })
      .addCase(updateRole.fulfilled, (state, action) => {
        state.rolesLoading = false;
        const index = state.roles.findIndex(r => r.id === action.payload.id);
        if (index !== -1) {
          state.roles[index] = action.payload;
        }
        if (state.selectedRole?.id === action.payload.id) {
          state.selectedRole = action.payload;
        }
        state.rolesError = null;
      })
      .addCase(updateRole.rejected, (state, action) => {
        state.rolesLoading = false;
        state.rolesError = action.payload;
      });

    // Delete Role
    builder
      .addCase(deleteRole.pending, (state) => {
        state.rolesLoading = true;
        state.rolesError = null;
      })
      .addCase(deleteRole.fulfilled, (state, action) => {
        state.rolesLoading = false;
        state.roles = state.roles.filter(r => r.id !== action.payload);
        if (state.selectedRole?.id === action.payload) {
          state.selectedRole = null;
        }
        state.rolesError = null;
      })
      .addCase(deleteRole.rejected, (state, action) => {
        state.rolesLoading = false;
        state.rolesError = action.payload;
      });

    // Fetch Role Users
    builder
      .addCase(fetchRoleUsers.pending, (state) => {
        state.roleUsersLoading = true;
        state.roleUsersError = null;
      })
      .addCase(fetchRoleUsers.fulfilled, (state, action) => {
        state.roleUsersLoading = false;
        state.roleUsers = action.payload.users;
        state.roleUsersError = null;
      })
      .addCase(fetchRoleUsers.rejected, (state, action) => {
        state.roleUsersLoading = false;
        state.roleUsersError = action.payload;
      });

    // ==================== ADMIN KULLANICI YÖNETİMİ ====================
    
    // Fetch Admin Users
    builder
      .addCase(fetchAdminUsers.pending, (state) => {
        state.adminUsersLoading = true;
        state.adminUsersError = null;
      })
      .addCase(fetchAdminUsers.fulfilled, (state, action) => {
        state.adminUsersLoading = false;
        state.adminUsers = action.payload.data;
        state.adminUsersPagination = action.payload.pagination;
        state.adminUsersError = null;
      })
      .addCase(fetchAdminUsers.rejected, (state, action) => {
        state.adminUsersLoading = false;
        state.adminUsersError = action.payload;
      });

    // Fetch Admin User By ID
    builder
      .addCase(fetchAdminUserById.pending, (state) => {
        state.adminUsersLoading = true;
        state.adminUsersError = null;
      })
      .addCase(fetchAdminUserById.fulfilled, (state, action) => {
        state.adminUsersLoading = false;
        state.selectedAdminUser = action.payload;
        state.adminUsersError = null;
        // Güncellenmiş kullanıcıyı listede de güncelle
        const index = state.adminUsers.findIndex(u => u.id === action.payload.id);
        if (index !== -1) {
          state.adminUsers[index] = action.payload;
        }
      })
      .addCase(fetchAdminUserById.rejected, (state, action) => {
        state.adminUsersLoading = false;
        state.adminUsersError = action.payload;
      });

    // Create Admin User
    builder
      .addCase(createAdminUser.pending, (state) => {
        state.adminUsersLoading = true;
        state.adminUsersError = null;
      })
      .addCase(createAdminUser.fulfilled, (state, action) => {
        state.adminUsersLoading = false;
        state.adminUsers = [action.payload, ...state.adminUsers];
        state.adminUsersError = null;
      })
      .addCase(createAdminUser.rejected, (state, action) => {
        state.adminUsersLoading = false;
        state.adminUsersError = action.payload;
      });

    // Update Admin User
    builder
      .addCase(updateAdminUser.pending, (state) => {
        state.adminUsersLoading = true;
        state.adminUsersError = null;
      })
      .addCase(updateAdminUser.fulfilled, (state, action) => {
        state.adminUsersLoading = false;
        const index = state.adminUsers.findIndex(u => u.id === action.payload.id);
        if (index !== -1) {
          state.adminUsers[index] = action.payload;
        }
        if (state.selectedAdminUser?.id === action.payload.id) {
          state.selectedAdminUser = action.payload;
        }
        state.adminUsersError = null;
      })
      .addCase(updateAdminUser.rejected, (state, action) => {
        state.adminUsersLoading = false;
        state.adminUsersError = action.payload;
      });

    // Delete Admin User
    builder
      .addCase(deleteAdminUser.pending, (state) => {
        state.adminUsersLoading = true;
        state.adminUsersError = null;
      })
      .addCase(deleteAdminUser.fulfilled, (state, action) => {
        state.adminUsersLoading = false;
        state.adminUsers = state.adminUsers.filter(u => u.id !== action.payload);
        if (state.selectedAdminUser?.id === action.payload) {
          state.selectedAdminUser = null;
        }
        state.adminUsersError = null;
      })
      .addCase(deleteAdminUser.rejected, (state, action) => {
        state.adminUsersLoading = false;
        state.adminUsersError = action.payload;
      });

    // Assign Roles To User
    builder
      .addCase(assignRolesToUser.fulfilled, (state, action) => {
        const { userId, roles } = action.payload;
        const index = state.adminUsers.findIndex(u => u.id === userId);
        if (index !== -1) {
          state.adminUsers[index].roles = roles;
        }
        if (state.selectedAdminUser?.id === userId) {
          state.selectedAdminUser.roles = roles;
        }
      });

    // Remove Role From User
    builder
      .addCase(removeRoleFromUser.fulfilled, (state, action) => {
        const { userId, roleId } = action.payload;
        const index = state.adminUsers.findIndex(u => u.id === userId);
        if (index !== -1 && state.adminUsers[index].roles) {
          state.adminUsers[index].roles = state.adminUsers[index].roles.filter(r => r.id !== roleId);
        }
        if (state.selectedAdminUser?.id === userId && state.selectedAdminUser.roles) {
          state.selectedAdminUser.roles = state.selectedAdminUser.roles.filter(r => r.id !== roleId);
        }
      });

    // ==================== PERMISSION YÖNETİMİ ====================
    
    // Fetch Permissions
    builder
      .addCase(fetchPermissions.pending, (state) => {
        state.permissionsLoading = true;
        state.permissionsError = null;
      })
      .addCase(fetchPermissions.fulfilled, (state, action) => {
        state.permissionsLoading = false;
        state.permissions = action.payload;
        state.permissionsError = null;
      })
      .addCase(fetchPermissions.rejected, (state, action) => {
        state.permissionsLoading = false;
        state.permissionsError = action.payload;
      });
  },
});

export const { clearRolesError, clearAdminUsersError, setSelectedRole, setSelectedAdminUser } = adminSlice.actions;

export default adminSlice.reducer;

